import { firestore } from "../config";
import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import dayjs from "dayjs";
import { ModelReport, ModelReportStatus, ModelReportType } from "../../../models/ModelReport";
import { getRemoteNumber, RemotePrams } from "../remote_config";
import { ensureRateLimitUid } from "./tributes";

// A one-line "this is spam" comment doesn't give a reviewer (super admin or
// the memorial's own owner/managers) enough to act on - this is enforced
// client-side only (form validation in ReportMemorial.tsx/ReportTribute.tsx),
// not a Firestore rule, same as the rate limit's client-side hint vs its
// real server-side enforcement above.
export const MIN_REPORT_COMMENT_CHARS = 30;

// SUPER ADMIN

// Security-rule-enforced admin check - a doc at admins/{uid} exists iff that
// user is a super admin. Firestore rules can't read Remote Config, so this
// (not the iOS app's admin_ids Remote Config list) is the real access
// boundary for reports; the iOS app is being updated to read from the same
// place. Managed entirely via the Firebase Console's Firestore data tab.
export const isSuperAdmin = async (uid: string | undefined) => {
  if (!uid) return false;
  return getDoc(doc(collection(firestore, "admins"), uid))
    .then((d) => d.exists())
    .catch(() => false);
};

// SUBMITTING A REPORT

const cooldownKey = (targetId: string) => `report_cooldown_${targetId}`;

export const isReportOnCooldown = (targetId: string) =>
  dayjs(Number(localStorage.getItem(cooldownKey(targetId))) || 0).isAfter(dayjs());

// public/{memorialId}/report_limits/{uid} holds only a last_submitted server
// timestamp - same shape and same purpose as tributes.tsx's tribute_limits,
// so the Firestore rule on reports/{id} create can enforce a real per-visitor
// cooldown (see ensureRateLimitUid) instead of trusting the client-side
// localStorage hint below, which a visitor can just clear in devtools.
const reportLimitDoc = (memorialId: string, uid: string) =>
  doc(collection(firestore, "public", memorialId, "report_limits"), uid);

// A same-device, same-browser cooldown hint only (Remote Config
// cooloff_hrs_reports, same key/default as iOS) - lets the button show a
// friendly message before opening the form, rather than always opening it
// and only failing at the very end. NOT the security boundary - see
// report_limits/ensureRateLimitUid above and the Firestore rule for that.
export const createMemorialReport = async (
  memorialId: string,
  comment: string,
  reporterId?: string,
  options?: { type: ModelReportType; tributeId: string }
) => {
  const targetId = options?.tributeId ?? memorialId;
  if (isReportOnCooldown(targetId)) return false;

  const uid = await ensureRateLimitUid();
  const data: Omit<ModelReport, "id"> = {
    memorial_id: memorialId,
    type: options?.type ?? ModelReportType.MEMORIAL,
    ...(options?.tributeId ? { tribute_id: options.tributeId } : {}),
    ...(comment ? { comment } : {}),
    status: ModelReportStatus.OPEN,
    ...(reporterId ? { reporter_id: reporterId } : {}),
    created: Timestamp.fromDate(new Date()),
    updated: Timestamp.fromDate(new Date()),
  };
  const reportRef = doc(collection(firestore, "public", memorialId, "reports"));
  const batch = writeBatch(firestore);
  batch.set(reportRef, data);
  batch.set(reportLimitDoc(memorialId, uid), { last_submitted: serverTimestamp() });

  try {
    await batch.commit();
  } catch (e: any) {
    // The rate limit is the only per-request condition on this write (see
    // the Firestore rule), so a rejection here is treated as the cooldown -
    // same convention as tributes.tsx's createTribute.
    if (e.code === "permission-denied") return false;
    throw e;
  }

  const cooloffHours = getRemoteNumber(RemotePrams.COOLOFF_HRS_REPORTS) || 1;
  localStorage.setItem(cooldownKey(targetId), `${dayjs().add(cooloffHours, "hours").valueOf()}`);
  return true;
};

// REVIEWING REPORTS

// Reports on a single memorial - for that memorial's own owner/managers.
export const getMemorialReports = async (memorialId: string) => {
  return getDocs(query(collection(firestore, "public", memorialId, "reports"), orderBy("created", "desc")))
    .then((d) => d.docs.map((ds) => ({ ...(ds.data() as ModelReport), id: ds.id })))
    .catch((e) => {
      console.error(e.message);
      return [] as ModelReport[];
    });
};

// A count-only aggregate query (no documents downloaded) - used by the
// Memorials list's open-reports badge, which previously called
// getMemorialReports and threw away everything but the count, downloading
// every report on every approved/hidden memorial just to size a badge.
export const getMemorialOpenReportCount = async (memorialId: string) => {
  return getCountFromServer(
    query(collection(firestore, "public", memorialId, "reports"), where("status", "==", ModelReportStatus.OPEN))
  )
    .then((snap) => snap.data().count)
    .catch((e) => {
      console.error(e.message);
      return 0;
    });
};

// Every report across every memorial - super admins only (enforced by the
// same per-document rule the direct subcollection read uses).
export const getAllReports = async () => {
  return getDocs(query(collectionGroup(firestore, "reports"), orderBy("created", "desc")))
    .then((d) => d.docs.map((ds) => ({ ...(ds.data() as ModelReport), id: ds.id })))
    .catch((e) => {
      console.error(e.message);
      return [] as ModelReport[];
    });
};

export const updateReportStatus = async (memorialId: string, reportId: string, status: ModelReportStatus) =>
  updateDoc(doc(collection(firestore, "public", memorialId, "reports"), reportId), {
    status,
    updated: serverTimestamp(),
  });

// Super-admin only - see the Firestore rule for reports/{reportId}, which
// gates delete the same way it gates the collection group read above.
export const deleteReport = async (memorialId: string, reportId: string) =>
  deleteDoc(doc(collection(firestore, "public", memorialId, "reports"), reportId));
